import { useEffect, useRef } from 'react';
import loadable from '@loadable/component';
import { useLocation, useParams } from 'react-router-dom';
import type { IGetProps, PageFC } from '@salesforce/pwa-kit-react-sdk';
import { ssrBffGet, useBffGet } from '../../bff-api/bff';
import { BFFHttpError } from '../../bff-api/model/BFFHttpError';
import { BffQueryKey } from '../../bff-api/model/BffQueryKey';
import { Layout } from '../../components/layout/Layout';
import { buildHttpError } from '../../utils/error-utils';
import { isServer } from '../../utils/utils';
import { configHtmlCache } from '../utils/config-html-cache';
import { PageUrl } from '../utils/page-url';
import { RDPSkeleton } from './RDPSkeleton';
import type { Recipe } from '../../bff-api/products/model/Recipe';

const RDP = loadable(() => import('./RDP'), {
    resolveComponent: (mod) => mod.RDP,
    fallback: <RDPSkeleton />,
});

const RDPPrint = loadable(() => import('./RDPPrint'), {
    resolveComponent: (mod) => mod.RDPPrint,
    fallback: <RDPSkeleton />,
});

const PRINT_RECIPE_PATH_PREFIX = PageUrl.PRINT_RDP.replace(/\/:[^/]+\/:[^/]+$/, '');

type RDPLazyProps = {
    recipe?: Recipe;
};

const RDPLazy: PageFC<RDPLazyProps> = ({ recipe: serverRecipe }: RDPLazyProps) => {
    const headerRef = useRef<HTMLDivElement | null>(null);
    const { recipeSku } = useParams<{ recipeSku?: string }>();
    const location = useLocation();
    const isPrintRoute = location.pathname.startsWith(PRINT_RECIPE_PATH_PREFIX);

    // Reset scroll when toggling between recipe and print routes so each view
    // always starts at the top instead of preserving the prior scroll position.
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [location.pathname, location.search]);

    // Fetch recipe on client side if not available from SSR
    const { data: clientRecipe, isLoading } = useBffGet<Recipe>(
        recipeSku ? `/recipes/${recipeSku}` : null,
        [],
        [BffQueryKey.PRODUCT, recipeSku ?? ''],
        {
            enabled: !isServer && !serverRecipe && !!recipeSku,
            initialData: serverRecipe,
            retry: false,
            headers: {
                'Cache-Control': 'no-store',
            },
        },
    );

    const recipe = serverRecipe || clientRecipe;

    // Show skeleton while loading or if recipe is not available
    if (!recipe || isLoading) {
        return (
            <Layout headerRef={headerRef}>
                <RDPSkeleton />
            </Layout>
        );
    }

    return isPrintRoute ? (
        <RDPPrint recipe={recipe} />
    ) : (
        <Layout headerRef={headerRef}>
            <RDP recipe={recipe} headerRef={headerRef} />
        </Layout>
    );
};

RDPLazy.shouldGetProps = (): Promise<boolean> => Promise.resolve(isServer);

RDPLazy.getProps = async ({ params, res }: IGetProps): Promise<RDPLazyProps> => {
    const { recipeSku } = params;

    if (!recipeSku) {
        const error = new BFFHttpError(404, 'Recipe Not Found', {});
        throw buildHttpError(error);
    }

    try {
        const recipe = await ssrBffGet<Recipe>(`/recipes/${recipeSku}`, {
            headers: {
                'Cache-Control': 'public, max-age=3600, stale-while-revalidate=86400',
            },
        });

        if (!recipe) {
            const error = new BFFHttpError(404, 'Recipe Not Found', {});
            throw buildHttpError(error);
        }

        configHtmlCache(res);

        return { recipe };
    } catch (e) {
        // Signal recipe fetch failure with consistent 404 error
        // Convert all errors (including 500s from BFF) to 404 for better UX
        const error = new BFFHttpError(404, 'Recipe BFF Fetch Error', {});
        throw buildHttpError(error);
    }
};

RDPLazy.defaultProps = {
    recipe: undefined,
};

export { RDPLazy };
